import type { Track } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { isBefore, isNonNil, now, strongFromEntries } from '@shady-pines/types';
import { isEmpty, isNil, last } from 'lodash';
import type { StoreApi } from 'zustand';
import { supabaseApi } from '../api/api-store';
import type { TrackState } from './types';

export type TrackApi = ReturnType<typeof TrackApi>;

export const TrackApi = (
  get: StoreApi<TrackState>['getState'],
  set: StoreApi<TrackState>['setState'],
) =>
  ({
    fetchMostRecentTrackHistory: async () => {
      const loading = get().loading;
      if (loading.mostRecentTrackHistory) {
        return;
      }
      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              mostRecentTrackHistory: true,
            },
          }) as const satisfies Partial<TrackState>,
      );
      const { data: newRecentTrackHistory } = await supabaseApi()
        .from('track_history_recent_public')
        .select('*')
        .limit(10);

      const recentTrackHistory = get().recentTrackHistory ?? [];

      const newTracksToAdd = (newRecentTrackHistory ?? []).filter(
        (n) => !recentTrackHistory.some((r) => r.id === n.id),
      );

      set(
        (s) =>
          ({
            recentTrackHistory: [...newTracksToAdd, ...recentTrackHistory].sort((a, b) =>
              isBefore(a.start_time, b.start_time) ? 1 : -1,
            ),
            loading: {
              ...s.loading,
              mostRecentTrackHistory: false,
            },
          }) as const satisfies Partial<TrackState>,
      );
    },
    fetchMoreRecentTrackHistory: async () => {
      console.log('we be fetchin mas at taco bell!');
      const loading = get().loading;
      if (loading.recentTrackHistory) {
        console.log('already loading, exiting');
        return;
      }
      const { fetchedFinalRecentTrackHistoryItem, recentTrackHistory, hasMoreTrackHistory } = get();
      if (!hasMoreTrackHistory || isNil(recentTrackHistory)) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              recentTrackHistory: true,
            },
          }) as const satisfies Partial<TrackState>,
      );

      const lastFetchedTrackHistoryStartTime = last(recentTrackHistory)?.start_time ?? now();

      const loadingFromTable = fetchedFinalRecentTrackHistoryItem
        ? 'track_history_full_public'
        : 'track_history_recent_public';

      console.log('some stuff we got', { lastFetchedTrackHistoryStartTime, loadingFromTable });

      const { data: more } = await supabaseApi()
        .from(loadingFromTable)
        .select('*')

        .lt('start_time', lastFetchedTrackHistoryStartTime)
        .filter('id', 'not.in', `(${recentTrackHistory.map((r) => r.id).join(',')})`)
        .limit(30);

      const moreTrackHistory = more ?? [];

      const newTracksToAdd = moreTrackHistory.filter(
        (n) => !recentTrackHistory.some((r) => r.id === n.id),
      );

      set(
        (s) =>
          ({
            recentTrackHistory: [...newTracksToAdd, ...recentTrackHistory].sort((a, b) =>
              isBefore(a.start_time, b.start_time) ? 1 : -1,
            ),
            fetchedFinalRecentTrackHistoryItem:
              loadingFromTable === 'track_history_recent_public'
                ? moreTrackHistory.length < 30
                : fetchedFinalRecentTrackHistoryItem,
            hasMoreTrackHistory:
              loadingFromTable === 'track_history_full_public'
                ? moreTrackHistory.length === 30
                : true,
            loading: {
              ...s.loading,
              recentTrackHistory: false,
            },
          }) as const satisfies Partial<TrackState>,
      );
    },

    fetchCurrentlyPlaying: async () => {
      const loading = get().loading;
      if (loading.currentlyPlaying) {
        return;
      }

      const oldCurrentlyPlaying = get().currentlyPlaying;
      set(
        (s) =>
          ({
            loading: { ...s.loading, currentlyPlaying: true },
          }) as const satisfies Partial<TrackState>,
      );
      const { data: newCurrentlyPlaying } = await supabaseApi()
        .from('current_track')
        .select('*')
        .maybeSingle();

      if (isNil(oldCurrentlyPlaying) || oldCurrentlyPlaying.id !== newCurrentlyPlaying?.id) {
        set({
          currentlyPlaying: newCurrentlyPlaying,
          currentlyAiringShow: newCurrentlyPlaying?.airing_show_id,
        } as const satisfies Partial<TrackState>);
        const { airing_show_id, show_history_id } = newCurrentlyPlaying ?? {};
        if (isNonNil(airing_show_id) && isNonNil(show_history_id)) {
          set(
            (s) =>
              ({
                trackHistoryByShowHistory: {
                  ...s.trackHistoryByShowHistory,
                  [show_history_id]: undefined,
                },
              }) as const satisfies Partial<TrackState>,
          );
        }
      }

      set(
        (s) =>
          ({
            loading: { ...s.loading, currentlyPlaying: false },
          }) as const satisfies Partial<TrackState>,
      );
    },

    fetchTrack: async (trackId: Uuid<Track>) => {
      const loading = get().loading;
      const tracksById = get().tracksById;
      if (isNonNil(tracksById[trackId])) {
        console.log('track already fetched', tracksById[trackId]);
        return;
      }
      console.log('track not fetched', loading.track[trackId]);
      if (loading.track[trackId]) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              track: {
                ...s.loading.track,
                [trackId]: true,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );

      const { data: track } = await supabaseApi()
        .from('tracks')
        .select('*')
        .eq('id', trackId)
        .single();
      if (isNil(track)) {
        return;
      }
      set(
        (s) =>
          ({
            tracksById: {
              ...s.tracksById,
              [trackId]: track,
            },
            loading: {
              ...s.loading,
              track: {
                ...s.loading.track,
                [trackId]: false,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );
    },

    fetchTracks: async (trackIds: Uuid<Track>[]) => {
      const loading = get().loading;

      const tracksById = get().tracksById;
      const missingTrackIds = trackIds.filter((id) => isNil(tracksById[id]));

      console.log('missing tracks', trackIds, tracksById);

      if (isEmpty(missingTrackIds)) {
        set(
          (s) =>
            ({
              loading: {
                ...s.loading,
                track: {
                  ...s.loading.track,
                  ...strongFromEntries(tracksToFetch.map((id) => [id, false])),
                },
              },
            }) as const satisfies Partial<TrackState>,
        );
        return;
      }

      console.log('tracks loading?', loading.track);

      const tracksToFetch = missingTrackIds.filter((id) => !loading.track[id]);

      console.log('tracks to fetch', tracksToFetch);

      if (isEmpty(tracksToFetch)) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              track: {
                ...s.loading.track,
                ...strongFromEntries(tracksToFetch.map((id) => [id, true])),
              },
            },
          }) as const satisfies Partial<TrackState>,
      );

      const { data: tracks } = await supabaseApi().from('tracks').select('*').in('id', trackIds);

      const newTrackData = strongFromEntries((tracks ?? []).map((t) => [t.id, t]));

      set(
        (s) =>
          ({
            tracksById: {
              ...s.tracksById,
              ...newTrackData,
            },
            loading: {
              ...s.loading,
              track: {
                ...s.loading.track,
                ...strongFromEntries(tracksToFetch.map((id) => [id, false])),
              },
            },
          }) as const satisfies Partial<TrackState>,
      );
    },
  }) as const;
